// src/components/AboutPageEditor.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import {
    doc,
    onSnapshot,
    setDoc,
    serverTimestamp,
} from 'firebase/firestore';
import {
    ref,
    uploadBytesResumable,
    getDownloadURL,
    deleteObject,
} from 'firebase/storage';
import { useAuth } from '../AuthProvider';
import '../styles/BellaBizarreStyle.css';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

/**
 * Firestore document:
 *   Collection: siteContent
 *   Doc:        about
 *   Fields:
 *     aboutText: string
 *     aboutImageUrl: string
 *     aboutImagePath: string
 *     updatedAt: serverTimestamp
 *     updatedBy: string (uid)
 */
export default function AboutPageEditor() {
    const { user } = useAuth();

    const [aboutText, setAboutText] = useState('');
    const [aboutImageUrl, setAboutImageUrl] = useState('');
    const [aboutImagePath, setAboutImagePath] = useState('');
    const [newFile, setNewFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(null);
    const [message, setMessage] = useState('');
    const fileRef = useRef(null);

    // Load current about content
    useEffect(() => {
        const d = doc(db, 'siteContent', 'about');
        const unsub = onSnapshot(d, (snap) => {
            const data = snap.data();
            if (data) {
                setAboutText(data.aboutText || '');
                setAboutImageUrl(data.aboutImageUrl || '');
                setAboutImagePath(data.aboutImagePath || '');
            }
        });
        return unsub;
    }, []);

    if (!user) {
        return (
            <div className="event-uploader-container">
                <p>Please <Link to="/login">log in</Link> to manage the About page.</p>
            </div>
        );
    }

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (f && f.size > MAX_FILE_SIZE) {
            setMessage('File exceeds 100MB.');
            if (fileRef.current) fileRef.current.value = '';
            return;
        }
        setNewFile(f || null);
        setMessage('');
    };

    const removeImage = async () => {
        if (!aboutImagePath) {
            await setDoc(
                doc(db, 'siteContent', 'about'),
                { aboutImageUrl: '', aboutImagePath: '', updatedAt: serverTimestamp(), updatedBy: user.uid },
                { merge: true }
            );
            setAboutImageUrl('');
            setAboutImagePath('');
            return;
        }
        if (!window.confirm('Remove current about image?')) return;
        try {
            await deleteObject(ref(storage, aboutImagePath));
            await setDoc(
                doc(db, 'siteContent', 'about'),
                { aboutImageUrl: '', aboutImagePath: '', updatedAt: serverTimestamp(), updatedBy: user.uid },
                { merge: true }
            );
            setAboutImageUrl('');
            setAboutImagePath('');
            setMessage('Image removed.');
        } catch (err) {
            console.error(err);
            setMessage('Failed to remove image.');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        setProgress(null);

        let finalUrl = aboutImageUrl;
        let finalPath = aboutImagePath;

        try {
            if (newFile) {
                // Replace prior file if any
                if (finalPath) {
                    try { await deleteObject(ref(storage, finalPath)); } catch (_) {}
                }
                const path = `site/about/about_${Date.now()}_${newFile.name}`;
                const storageRef = ref(storage, path);
                const task = uploadBytesResumable(storageRef, newFile, { contentType: newFile.type });

                await new Promise((resolve, reject) => {
                    task.on(
                        'state_changed',
                        (snap) => {
                            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
                            setProgress(pct);
                        },
                        reject,
                        async () => {
                            finalUrl = await getDownloadURL(task.snapshot.ref);
                            finalPath = path;
                            resolve();
                        }
                    );
                });
            }

            await setDoc(
                doc(db, 'siteContent', 'about'),
                {
                    aboutText: (aboutText || '').trim(),
                    aboutImageUrl: finalUrl || '',
                    aboutImagePath: finalPath || '',
                    updatedAt: serverTimestamp(),
                    updatedBy: user.uid,
                },
                { merge: true }
            );
            setMessage('About page updated.');
            setNewFile(null);
            if (fileRef.current) fileRef.current.value = '';
        } catch (err) {
            console.error(err);
            setMessage(err.message || 'Save failed.');
        } finally {
            setSaving(false);
            setProgress(null);
        }
    };

    return (
        <div className="event-uploader-container">
            <h2>About Page Editor</h2>
            <form onSubmit={handleSave} className="event-uploader-form">
                <label>
                    About Text:
                    <textarea
                        className="event-uploader-textarea"
                        value={aboutText}
                        onChange={(e) => setAboutText(e.target.value)}
                        placeholder="Write a short bio or introduction…"
                        rows={6}
                    />
                </label>

                <label>
                    About Image:
                    <input type="file" accept="image/*" onChange={handleFileChange} ref={fileRef} />
                </label>

                {progress !== null && <p>Uploading: {progress}%</p>}

                {aboutImageUrl && (
                    <div style={{ margin: '8px 0' }}>
                        <img src={aboutImageUrl} alt="Current about" className="gallery-image" />
                        <div className="event-uploader-card-buttons" style={{ marginTop: 8 }}>
                            <button type="button" className="event-uploader-delete-button" onClick={removeImage}>
                                Remove Image
                            </button>
                        </div>
                    </div>
                )}

                {message && <p className="event-uploader-message">{message}</p>}

                <button type="submit" disabled={saving} className="event-uploader-submit-button">
                    {saving ? 'Saving…' : 'Save Changes'}
                </button>
            </form>
        </div>
    );
}
